import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { submitEnquiry } from "../lib/api";
import { extractErrorMessage } from "../lib/errors";
import { Phone, Loader2 } from "lucide-react";

const DEFAULT_MESSAGE =
  "I would like to enquire about this intensive course. Please call me to discuss availability and timings.";

export default function IntensiveEnquireModal({ open, onOpenChange, courseName }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    postcode: "",
    message: DEFAULT_MESSAGE,
  });
  const [sending, setSending] = useState(false);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const reset = () => {
    setForm({ name: "", email: "", phone: "", postcode: "", message: DEFAULT_MESSAGE });
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const { name, email, phone, postcode, message } = form;
    if (!name.trim() || !email.trim() || !phone.trim() || !message.trim()) {
      toast.error("Please fill name, email, phone and message.");
      return;
    }
    const pc = postcode.trim().toUpperCase();
    if (!pc) {
      toast.error("Postcode is required.");
      return;
    }
    if (!/^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/.test(pc)) {
      toast.error("Please enter a valid UK postcode (e.g. SO15 2BG).");
      return;
    }
    try {
      setSending(true);
      await submitEnquiry({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        postcode: pc,
        subject: `Intensive course enquiry: ${courseName}`,
        course: courseName,
        source: "intensive_course",
        message: message.trim(),
      });
      toast.success(`Thanks ${name.split(" ")[0] || ""} — Akram will call you back shortly.`);
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not send enquiry"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="intensive-enquire-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }} className="text-2xl">
            Enquire about this course
          </DialogTitle>
          <DialogDescription>
            Fill in your details and Akram will call you back to confirm availability and timings.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3" data-testid="intensive-enquire-form">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Course</Label>
            <Input
              value={courseName}
              readOnly
              className="bg-[#F3F4F6] text-[#0A0F00] cursor-not-allowed font-medium"
              data-testid="intensive-enquire-course"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Full name</Label>
              <Input
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                autoFocus
                data-testid="intensive-enquire-name"
                required
              />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                type="tel"
                data-testid="intensive-enquire-phone"
                required
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              type="email"
              data-testid="intensive-enquire-email"
              required
            />
          </div>
          <div>
            <Label className="text-xs">Postcode</Label>
            <Input
              value={form.postcode}
              onChange={(e) => update("postcode", e.target.value.toUpperCase())}
              placeholder="SO14 1AB"
              data-testid="intensive-enquire-postcode"
              required
            />
          </div>
          <div>
            <Label className="text-xs">Message</Label>
            <Textarea
              rows={4}
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              data-testid="intensive-enquire-message"
              required
            />
          </div>
          <div className="flex items-center justify-between gap-3 pt-2">
            <a
              href="tel:+447472887780"
              className="text-xs text-[#4B5563] hover:text-[#37B160] inline-flex items-center gap-1.5"
              data-testid="intensive-enquire-call-link"
            >
              <Phone className="h-3.5 w-3.5" />
              Or call Akram on 07472 887780
            </a>
            <Button
              type="submit"
              disabled={sending}
              className="bg-[#37B160] hover:bg-[#2D9450] text-white"
              data-testid="intensive-enquire-submit"
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending…
                </>
              ) : (
                "Send enquiry"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
